import { useState, useEffect, useCallback } from "react";
import { ApiService } from "../services/api";
import { GameConfig } from "../types";
import { getBetOptions } from "../utils/betPreferences";

interface GameConfigState {
  config: GameConfig | null;
  loading: boolean;
  error: string | null;
}

export const useGameConfig = () => {
  const [state, setState] = useState<GameConfigState>({
    config: null,
    loading: false,
    error: null,
  });

  const fetchConfig = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const response: GameConfig = await ApiService.getGameConfig();
      setState({
        config: response,
        loading: false,
        error: null,
      });
    } catch (error) {
      console.error("Error fetching utility config:", error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : "Failed to fetch utility config",
      }));
    }
  }, []);

  // Fetch config on mount
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const getBetAmountOptions = useCallback(() => {
    return state.config?.betAmounts ?? getBetOptions();
  }, [state.config]);

  const isRowSkipEnabled = useCallback(() => {
    return state.config?.utilities.rowSkip.enabled ?? false;
  }, [state.config]);

  const isDXFMiningBoostEnabled = useCallback(() => {
    return state.config?.utilities.dxfMiningBoost.enabled ?? false;
  }, [state.config]);

  const isPhoenixRevivalEnabled = useCallback(() => {
    return state.config?.utilities.phoenixRevival.enabled ?? false;
  }, [state.config]);

  const getRowSkipPrice = useCallback((betAmountBnb: string) => {
    return Number(state.config?.utilities.rowSkip.basePrice) * Number(state.config?.utilities.betAmountFactor[betAmountBnb]);
  }, [state.config]);

  const getDXFMiningBoostPrice = useCallback((betAmountBnb: string) => {
    return Number(state.config?.utilities.dxfMiningBoost.basePrice) * Number(state.config?.utilities.betAmountFactor[betAmountBnb]);
  }, [state.config]);

  const getPhoenixRevivalPrice = useCallback((betAmountBnb: string) => {
    return Number(state.config?.utilities.phoenixRevival.basePrice) * Number(state.config?.utilities.betAmountFactor[betAmountBnb]);
  }, [state.config]);

  const getDXFMiningBoostMaxCount = useCallback(() => {
    return state.config?.utilities.dxfMiningBoost.maxCount ?? 10;
  }, [state.config]);

  const getRowSkipDescription = useCallback(() => {
    return state.config?.utilities.rowSkip.description ?? "Skip one dangerous row of your choice during gameplay";
  }, [state.config]);

  const getDXFMiningBoostDescription = useCallback(() => {
    return state.config?.utilities.dxfMiningBoost.description ?? "Increase DXF reward rate by 10% per boost (stackable)";
  }, [state.config]);

  const getPhoenixRevivalDescription = useCallback(() => {
    return state.config?.utilities.phoenixRevival.description ?? "Get a second chance after hitting a death tile (multiplier resets to 1x)";
  }, [state.config]);

  return {
    config: state.config,
    loading: state.loading,
    error: state.error,
    refetch: fetchConfig,
    getBetAmountOptions,
    isRowSkipEnabled,
    isDXFMiningBoostEnabled,
    isPhoenixRevivalEnabled,
    getRowSkipPrice,
    getDXFMiningBoostPrice,
    getPhoenixRevivalPrice,
    getDXFMiningBoostMaxCount,
    getRowSkipDescription,
    getDXFMiningBoostDescription,
    getPhoenixRevivalDescription,
  };
};